import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';

import { getSession } from '@/actions/session';
import { fetchUser } from '@/actions/user';
import {
  clearAuthFailure,
  isAuthFailed,
  isUsableSession,
  recoverAuth,
} from '@/services/auth';
import { fetchGpuClasses } from '@/actions/gpuClasses';
import { fetchProjects } from '@/actions/projects';
import {
  SET_SESSION,
  CLEAR_SESSION,
  FETCH_GPU_CLASSES,
  SET_USER,
  DELETE_USER,
} from '@/actions/types';
import {
  AuthProvider,
  useAuth,
  useAuthDispatch,
  UserProvider,
  useUser,
  useUserDispatch,
  GpuClassesProvider,
  useGpuClassesDispatch,
} from '@/providers';
import Header from './Header';

function LayoutContent() {
  const dispatch = useDispatch(); // Still needed for fetchProjects
  const location = useLocation();
  const session = useAuth();
  const user = useUser();
  const authDispatch = useAuthDispatch();
  const userDispatch = useUserDispatch();
  const gpuClassesDispatch = useGpuClassesDispatch();
  const [drawerOpen, setDrawerOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [drawerPinned, setDrawerPinned] = useState(() => {
    const saved = localStorage.getItem('sidebarPinned');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const timerIdRef = useRef(undefined);
  const fetchedSessionIdRef = useRef(null);
  const fetchedUserIdRef = useRef(null);
  const sessionExpiresRef = useRef(null);
  const explicitlyLoggedOutRef = useRef(false);
  const idTokenRef = useRef(null);
  explicitlyLoggedOutRef.current = Boolean(session?.explicitlyLoggedOut);
  idTokenRef.current = session?.credentials?.idToken || null;

  const applySession = useCallback(
    (session_data) => {
      if (explicitlyLoggedOutRef.current) {
        return;
      }
      if (isUsableSession(session_data)) {
        if (session_data.credentials.idToken === idTokenRef.current) {
          return;
        }
        clearAuthFailure();
        authDispatch({ type: SET_SESSION, session: session_data });
        return;
      }
      // A failed refresh of the hourly id token is not a logout while the
      // refresh token is still stored. Only a rejected refresh token signs in.
      if (!isAuthFailed()) {
        return;
      }
      authDispatch({ type: CLEAR_SESSION });
      userDispatch({ type: DELETE_USER });
      recoverAuth();
    },
    [authDispatch, userDispatch],
  );

  // Check for active Cognito session on mount and when session changes
  // Skip if we're on the landing page (allow users to stay logged out there)
  useEffect(() => {
    if (
      location.pathname === '/' ||
      location.pathname === '/logout' ||
      location.pathname === '/auth/callback'
    ) {
      return;
    }
    // Don't try to restore session if user explicitly logged out
    if (session?.explicitlyLoggedOut || session?.isLoggedIn) {
      return;
    }
    let cancelled = false;
    let retry_timer;
    const attempt = () => {
      getSession().then((session_data) => {
        if (cancelled) {
          return;
        }
        if (isUsableSession(session_data) || isAuthFailed()) {
          applySession(session_data);
          return;
        }
        retry_timer = setTimeout(attempt, 30 * 1000);
      });
    };
    attempt();
    return () => {
      cancelled = true;
      if (retry_timer) {
        clearTimeout(retry_timer);
      }
    };
  }, [
    session?.isLoggedIn,
    session?.explicitlyLoggedOut,
    applySession,
    location.pathname,
  ]);

  // Fetch user and projects when session changes, but only if we haven't fetched for this session yet
  useEffect(() => {
    if (session?.isLoggedIn && isUsableSession(session) && !isAuthFailed()) {
      // Tokens live under credentials; idToken changes when the session refreshes
      const session_id = session.credentials?.idToken || session.id;

      // Only fetch if this is a new session (different from what we've already fetched)
      if (fetchedSessionIdRef.current !== session_id) {
        fetchedSessionIdRef.current = session_id;
        fetchUser()
          .then((userData) => {
            if (userData) {
              userDispatch({ type: SET_USER, payload: userData });
              return;
            }
            if (isAuthFailed()) {
              applySession(null);
              return;
            }
            fetchedSessionIdRef.current = null;
          })
          .catch(() => {
            // Non-auth errors are logged in fetchUser. Leave the session in place.
          });
        dispatch(fetchProjects()).catch(() => {
          // Project load errors are reported by the action. Don't retry from here.
        });
      }
    } else if (!session?.isLoggedIn) {
      // Reset the ref when logged out
      fetchedSessionIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Intentionally using specific session properties instead of entire session object
    // to prevent infinite loops when session refreshes with new object reference
  }, [
    session?.credentials?.idToken,
    session?.expires,
    session?.id,
    session?.isLoggedIn,
    userDispatch,
    applySession,
    dispatch,
  ]);

  // Fetch GPU classes when user changes, but only if user.id actually changed
  useEffect(() => {
    if (user?.id) {
      // Only fetch if this is a different user ID than what we've already fetched
      if (fetchedUserIdRef.current !== user.id) {
        fetchedUserIdRef.current = user.id;
        fetchGpuClasses(user.id)
          .then((gpuClassesData) => {
            gpuClassesDispatch({
              type: FETCH_GPU_CLASSES,
              payload: gpuClassesData,
            });
          })
          .catch(() => {
            // Error handling is done in fetchGpuClasses
          });
      }
    } else {
      // Reset the ref when user is cleared
      fetchedUserIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Intentionally using user.id instead of entire user object to prevent infinite loops
    // when user object reference changes but ID remains the same
  }, [user?.id, gpuClassesDispatch]);

  // Automatically refresh tokens before expiration (transparent refresh)
  // Refresh 5 minutes before expiration to ensure seamless experience
  useEffect(() => {
    if (session?.isLoggedIn && session.expires && !isAuthFailed()) {
      const expiration_time = session.expires * 1000; // Convert to milliseconds

      // Only set up a new timer if the expiration time has changed
      if (sessionExpiresRef.current !== expiration_time) {
        // Clear any existing timer
        if (timerIdRef.current) {
          clearTimeout(timerIdRef.current);
          timerIdRef.current = undefined;
        }

        sessionExpiresRef.current = expiration_time;
        const current_time = Date.now();
        const refresh_buffer = 5 * 60 * 1000; // 5 minutes in milliseconds
        const delay = expiration_time - current_time - refresh_buffer;

        const refresh_session = () => {
          if (isAuthFailed()) {
            return;
          }
          getSession(true).then((session_data) => {
            if (isUsableSession(session_data)) {
              if (session_data.expires * 1000 === expiration_time) {
                const remaining = expiration_time - Date.now();
                if (remaining > 0) {
                  timerIdRef.current = setTimeout(() => {
                    timerIdRef.current = undefined;
                    sessionExpiresRef.current = null;
                    refresh_session();
                  }, remaining);
                  return;
                }
              }
              applySession(session_data);
              return;
            }
            if (isAuthFailed()) {
              applySession(null);
              return;
            }
            // Id token could not be renewed yet. Keep the refresh token and try again.
            timerIdRef.current = setTimeout(() => {
              timerIdRef.current = undefined;
              sessionExpiresRef.current = null;
              refresh_session();
            }, 60 * 1000);
          });
        };

        if (delay > 0) {
          timerIdRef.current = setTimeout(() => {
            timerIdRef.current = undefined;
            refresh_session();
          }, delay);
        } else {
          // Token already expired or will expire soon, force refresh immediately
          refresh_session();
        }
      }
    } else {
      // Clear timer and reset expiration ref when logged out
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
        timerIdRef.current = undefined;
      }
      sessionExpiresRef.current = null;
    }

    return () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
        timerIdRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Intentionally using specific session properties instead of entire session object
    // to prevent infinite loops when session refreshes with new object reference
  }, [session?.expires, session?.isLoggedIn, applySession]);

  // Force-refresh session when a frozen/background tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState !== 'visible' ||
        !session?.isLoggedIn ||
        isAuthFailed()
      ) {
        return;
      }

      getSession(false).then((session_data) => {
        if (isUsableSession(session_data)) {
          applySession(session_data);
          return;
        }
        if (isAuthFailed()) {
          applySession(null);
        }
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [session?.isLoggedIn, applySession]);

  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(drawerOpen));
  }, [drawerOpen]);

  useEffect(() => {
    localStorage.setItem('sidebarPinned', JSON.stringify(drawerPinned));
  }, [drawerPinned]);

  // Google Analytics tracking
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', import.meta.env.VITE_GTAG, {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  const toggleDrawer = () => {
    if (!drawerPinned) {
      setDrawerOpen(!drawerOpen);
    }
  };

  const togglePin = () => {
    setDrawerPinned(!drawerPinned);
    if (!drawerPinned) {
      setDrawerOpen(true);
    }
  };

  return (
    <Box>
      <Header
        open={drawerOpen}
        toggleDrawer={toggleDrawer}
        pinned={drawerPinned}
      />
      <Outlet
        context={{ drawerOpen, toggleDrawer, pinned: drawerPinned, togglePin }}
      />
      <ScrollRestoration />
    </Box>
  );
}

function Layout() {
  return (
    <AuthProvider>
      <UserProvider>
        <GpuClassesProvider>
          <LayoutContent />
        </GpuClassesProvider>
      </UserProvider>
    </AuthProvider>
  );
}

export default Layout;
